import { createApp } from 'vue';
import axios from 'axios';
import App from './App.vue';
import router from './routes';
import { getPlanStatus } from './utils/auth';
import Toast, { POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";
import { reveal } from './marketing/directives/reveal';
import { isAdmin, isViewOnly, refreshAccessToken, getRefreshToken } from './utils/auth';
import { showBanner } from './utils/statusBanner';

// Endpoints that must NOT trigger a silent refresh/retry on 401 (they ARE the
// auth flow, or a 401 from them is terminal).
const AUTH_ENDPOINTS = ['/oauth/token', '/token', '/oauth/authorize', '/oauth/logout'];
function isAuthEndpoint(url) {
    const u = (url || '').toLowerCase();
    return AUTH_ENDPOINTS.some(p => u.includes(p));
}

// Centralized Axios error handler
function handleAxiosError(error) {
    // Requests cancelled by the view-only guard already alerted the user.
    if (axios.isCancel && axios.isCancel(error)) {
        return Promise.reject(error);
    }
    if (error.response) {

        const status = error.response.status;
        const errorCode = error.response.data?.error_code;
        const message = error.response.data?.message || error.response.data?.detail || error.message;

        // Backend rejects every request once a company's plan has expired, or
        // if no plan is assigned at all (see src/service/plan_service.py,
        // error_codes BIZ_012 / BIZ_015). Route to the dedicated full-screen
        // page instead of a generic alert.
        if (status === 403 && (errorCode === 'BIZ_012' || errorCode === 'BIZ_015')) {
            console.warn('Plan blocked:', errorCode, message);
            const newStatus = errorCode === 'BIZ_015' ? 'NO_PLAN' : 'EXPIRED';
            if (getPlanStatus() !== newStatus) {
                // Backend is the source of truth; refresh the cached copy so the
                // router guard also recognizes the blocked state on next navigation.
                try {
                    const info = JSON.parse(localStorage.getItem('user-info') || '{}');
                    info.plan_status = newStatus;
                    localStorage.setItem('user-info', JSON.stringify(info));
                } catch (e) { /* ignore malformed cache */ }
            }
            if (router.currentRoute.value.name !== 'PlanExpired') {
                router.push({ name: 'PlanExpired', query: { reason: errorCode === 'BIZ_015' ? 'no_plan' : 'expired' } });
            }
            return Promise.reject(error);
        }

        // Callers can opt out of the global alert (e.g. to render the error
        // inline on a form) by setting `suppressErrorAlert: true` on the request
        // config. We still reject so their own .catch handler runs.
        if (error.config && error.config.suppressErrorAlert && status !== 401) {
            console.warn('Suppressed error alert:', status, message);
            return Promise.reject(error);
        }

        switch (status) {
            case 400:
                console.warn('Bad Request:', message);
                showBanner('Bad request. ' + message, 'error');
                break;
            case 401:
                console.warn('Unauthorized:', message);
                showBanner('Session expired or unauthorized. Please log in again.', 'warning');
                localStorage.removeItem('user-info');
                window.location.href = '/login';
                break;
            case 403:
                console.warn('Forbidden:', message);
                showBanner('You do not have permission to perform this action.', 'error');
                break;
            case 404:
                console.warn('Not Found:', message);
                showBanner('Resource not found.', 'warning');
                break;
            case 422:
                console.warn('Validation error:', message);
                showBanner('Validation failed: ' + message, 'warning');
                break;
            case 500:
                console.error('Server error:', message);
                showBanner('Server error occurred. Please try again later.', 'error');
                break;
            default:
                console.error('HTTP error:', status, message);
                showBanner(`Error ${status}: ${message}`, 'error');
                break;
        }
    } else if (error.request) {
        console.error('No response received', error.request);
        showBanner('No response from server. Please check your network connection.', 'error');
    } else {
        console.error('Axios request error', error.message);
        showBanner('Request error: ' + error.message, 'error');
    }

    return Promise.reject(error);
}

// Response interceptor: on a 401 (expired/invalid access token) try to renew
// the token silently with the refresh token and replay the original request,
// so the user is never bounced to the login screen mid-session. Only if the
// refresh itself fails do we fall through to the normal handler (which logs
// out on 401).
axios.interceptors.response.use(
    response => response,
    async error => {
        const original = error.config;
        const status = error.response && error.response.status;

        if (
            status === 401 &&
            original &&
            !original._retriedAfterRefresh &&
            !isAuthEndpoint(original.url) &&
            getRefreshToken()
        ) {
            original._retriedAfterRefresh = true;   // retry only once
            try {
                const newToken = await refreshAccessToken();
                // Components attach the bearer header themselves, so overwrite it
                // (and the default) with the freshly minted token before replaying.
                original.headers = original.headers || {};
                original.headers.Authorization = 'Bearer ' + newToken;
                return axios(original);
            } catch (e) {
                // Refresh failed (expired/rotated/absent) — genuine logout.
                console.warn('Silent token refresh failed:', e && e.message);
                // Fall through to the standard 401 handling below.
            }
        }

        return handleAxiosError(error);
    }
);

// View-only enforcement (client mirror of the backend RoleAccessMiddleware).
// A logged-in non-admin may read but never mutate. We block mutating requests
// here so a viewer gets an instant, friendly message instead of a 403 round
// trip. The backend remains the real gate. Requests may opt out with
// `allowViewOnly: true` (none currently need to).
const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];
const VIEWER_ALLOWED = [
    '/token', '/oauth/token', '/oauth/logout', '/login', '/logout',
    '/profile/update', '/profile/reset-password', '/register', '/registration',
    '/user/verify-phone', '/whatsapp/send-registration-otp',
    // Viewers may keep their own bookmarks (add, remove, status).
    '/bookmarks',
];
function viewerRequestAllowed(config) {
    const method = (config.method || 'get').toLowerCase();
    if (!MUTATING_METHODS.includes(method)) return true;      // reads always ok
    if (config.allowViewOnly) return true;                    // explicit opt-out
    const url = (config.url || '').toLowerCase();
    // Self-service endpoints a viewer may still call.
    if (VIEWER_ALLOWED.some(p => url.includes(p))) return true;
    if (url.includes('/notifications/')) return true;         // mark-as-read
    return false;
}

axios.interceptors.request.use((config) => {
    if (isViewOnly() && !viewerRequestAllowed(config)) {
        const msg = 'You have view-only access. This action requires an administrator.';
        try { showBanner(msg, 'warning'); } catch (e) { /* non-browser */ }
        return Promise.reject(new axios.Cancel(msg));
    }
    return config;
});

const app = createApp(App);
app.config.globalProperties.$API_URL = process.env.VUE_APP_API_URL;
app.config.globalProperties.$handleAxiosError = handleAxiosError;
// Global colored status strip: $showBanner(message, 'error'|'warning'|'success'|'info')
app.config.globalProperties.$showBanner = showBanner;
// Role helpers available in every template as $isAdmin / $isViewOnly so
// components can hide add/edit/delete controls for viewers.
app.config.globalProperties.$isAdmin = isAdmin;
app.config.globalProperties.$isViewOnly = isViewOnly;

// Tag the root element so global CSS can dim/disable mutation controls marked
// with the `.admin-only` class for viewers.
function applyViewOnlyClass() {
    const el = document.documentElement;
    if (isViewOnly()) el.classList.add('is-view-only');
    else el.classList.remove('is-view-only');
}
applyViewOnlyClass();
window.addEventListener('storage', applyViewOnlyClass);
router.afterEach(applyViewOnlyClass);

app.use(router);
// Scroll-reveal directive used by the merged marketing pages.
app.directive('reveal', reveal);
app.use(Toast, {
  position: POSITION.TOP_RIGHT,
  timeout: 3000,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
});
app.mount('#app');
import './assets/css/main.css';
import './tender_style.css';
import './marketing/assets/styles/main.css';

